import { ReactNode, useCallback, useState } from "react"
import { Select } from "@oysterhr/ui"
import type { SelectProps, SelectSizes } from "@oysterhr/ui"
import { useExcludedSubdivisionsForCountry } from "./useExcludedStatesForCountry"
import { useSubdivisions, GetSubdivisionsQueryData } from "../useSubdivisions"

type SubdivisionSelectProps = {
  selectedCountry: string | undefined
  className?: string
  labelEndAdornment?: ReactNode
  assistiveText?: ReactNode
  isOptional?: boolean
  /** Only needed for testing. */
  onFetchedSubdivisions?: () => void
  size?: SelectSizes
  disableUnavailableSubdivisions?: boolean
  onChange: Exclude<SelectProps["onChange"], undefined>
} & SelectProps

/**
 * Administrative division (state/region/province) select.
 */
export const SubdivisionSelect = ({
  label,
  error: formError,
  disabled = false,
  selectedCountry,
  onFetchedSubdivisions,
  onChange,
  value,
  size,
  disableUnavailableSubdivisions,
  ...rest
}: SubdivisionSelectProps) => {
  const [searchValue, setSearchValue] = useState<string>("")
  const onQueryCompleted = useCallback(
    (data: GetSubdivisionsQueryData) => {
      if (onFetchedSubdivisions) onFetchedSubdivisions()
      // Needed to reset parent value after changing country
      if (!data?.country?.subdivisions.some(s => s.code === value)) onChange("")
    },
    [onFetchedSubdivisions, onChange, value],
  )
  const {
    error: queryError,
    data,
    loading,
    isSubdivisionsArrayEmpty,
  } = useSubdivisions(selectedCountry || "", {
    onCompleted: onQueryCompleted,
  })

  const filteredOptions =
    data?.country?.subdivisions.filter(subdivision =>
      subdivision.name.toLowerCase().includes(searchValue.toLowerCase()),
    ) || []

  const excludedSubdivisions = useExcludedSubdivisionsForCountry(
    disableUnavailableSubdivisions ? selectedCountry : "",
  )

  const optionsList =
    filteredOptions.map(({ name, code }) => {
      return (
        <Select.Option key={code} value={code} disabled={excludedSubdivisions.includes(code)}>
          {name}
        </Select.Option>
      )
    }) || []

  const componentError =
    formError ||
    (queryError && "An error occurred while fetching data, please refresh the page and try again.")

  if (isSubdivisionsArrayEmpty) return null
  return (
    <Select
      label={label}
      disabled={disabled || !selectedCountry || loading}
      error={formError || componentError}
      onChange={onChange}
      value={value}
      size={size}
      {...rest}
    >
      <Select.Input value={searchValue} onChange={e => setSearchValue(e.target.value)} />
      {optionsList}
    </Select>
  )
}
